import { useEffect, useRef } from 'react';

interface UseModalA11yProps {
  isOpen: boolean;
  onClose: () => void;
}

export function useModalA11y({ isOpen, onClose }: UseModalA11yProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      // 1. Armazenar o foco anterior
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focar no modal para iniciar o trap
      const focusDelay = setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 50);

      const handleKeyDown = (e: KeyboardEvent) => {
        // 2. Fechar com Escape
        if (e.key === 'Escape') {
          onClose();
          return;
        }

        // 3. Prender o foco com Tab
        if (e.key === 'Tab') {
          if (!modalRef.current) return;

          const focusableElements = modalRef.current.querySelectorAll(
            'a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );

          if (focusableElements.length === 0) {
            e.preventDefault();
            return;
          }

          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

          if (e.shiftKey) {
            // Se estiver no primeiro elemento e pressionar Shift+Tab, ir para o último
            if (document.activeElement === firstElement || document.activeElement === modalRef.current) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            // Se estiver no último elemento e pressionar Tab, ir para o primeiro
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        clearTimeout(focusDelay);
        document.removeEventListener('keydown', handleKeyDown);
        // 4. Restaurar o foco anterior
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
        }
      };
    }
  }, [isOpen, onClose]);

  return { modalRef };
}
